import {
  Badge,
  cn,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@devhop/ui";
import { Gift, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { posApi } from "../api/client";
import { hasLoyaltyRewardsReferenceCache } from "../lib/offline-sync/reference-cache";
import {
  listLocalFirstAffordableRewards,
  resolveLocalFirstLoyaltyRewardPreview,
} from "../lib/offline-sync/resolve-local-first-loyalty-reward";
import {
  loyaltyPreviewItemsKey,
  type LoyaltyPreviewItem,
} from "../lib/loyalty-preview-items";

type Props = {
  branchId: string | null;
  customerId?: string | null;
  orderId?: string | null;
  cartItems?: LoyaltyPreviewItem[];
  orderGrandTotal?: number;
  enabled?: boolean;
  className?: string;
  value: string | null;
  onChange: (rewardId: string | null) => void;
  onPreviewChange?: (preview: {
    discountPreview?: number;
    pointsCost: number;
    rewardName: string;
    eligible: boolean;
    snapshot?: import("../lib/offline-sync/types").LocalOrderRewardSnapshot;
    source?: "network" | "cache";
  } | null) => void;
};

function canUseNetwork(): boolean {
  return typeof navigator === "undefined" || navigator.onLine;
}

export function LoyaltyRewardPicker({
  branchId,
  customerId,
  orderId,
  cartItems = [],
  orderGrandTotal = 0,
  enabled = true,
  className,
  value,
  onChange,
  onPreviewChange,
}: Props) {
  const [cacheAvailable, setCacheAvailable] = useState<boolean | null>(null);
  const [rewards, setRewards] = useState<
    Array<{ id: string; name: string; pointsCost: number }>
  >([]);
  const [balance, setBalance] = useState<number | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const onPreviewChangeRef = useRef(onPreviewChange);
  onPreviewChangeRef.current = onPreviewChange;
  const cartItemsRef = useRef(cartItems);
  cartItemsRef.current = cartItems;
  const cartItemsKey = loyaltyPreviewItemsKey(cartItems);
  const orderGrandTotalKey = Math.round(orderGrandTotal * 100) / 100;

  const [preview, setPreview] = useState<{
    eligible: boolean;
    error?: string;
    pointsCost: number;
    balanceAfter: number;
    discountPreview?: number;
    rewardName: string;
    source: "network" | "cache";
    snapshot?: import("../lib/offline-sync/types").LocalOrderRewardSnapshot;
  } | null>(null);

  useEffect(() => {
    if (!branchId) {
      setCacheAvailable(false);
      return;
    }
    void hasLoyaltyRewardsReferenceCache(branchId).then(setCacheAvailable);
  }, [branchId]);

  useEffect(() => {
    if (!enabled || !branchId || !customerId) {
      setRewards([]);
      setBalance(null);
      return;
    }
    if (!canUseNetwork() && cacheAvailable === false) {
      setRewards([]);
      return;
    }

    let cancelled = false;
    setListLoading(true);
    void listLocalFirstAffordableRewards({
      branchId,
      customerId,
      cartItems: cartItemsRef.current,
      orderGrandTotal: orderGrandTotalKey,
    })
      .then((result) => {
        if (cancelled) return;
        setRewards(result.rewards);
        setBalance(result.balance);
      })
      .catch(() => {
        if (!cancelled) {
          setRewards([]);
        }
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    branchId,
    customerId,
    cacheAvailable,
    cartItemsKey,
    orderGrandTotalKey,
  ]);

  useEffect(() => {
    if (!enabled || !branchId || !customerId || !value) {
      setPreview(null);
      onPreviewChangeRef.current?.(null);
      return;
    }

    let cancelled = false;
    setPreviewLoading(true);
    void resolveLocalFirstLoyaltyRewardPreview({
      branchId,
      customerId,
      rewardId: value,
      orderId: orderId ?? undefined,
      cartItems: cartItemsRef.current,
      orderGrandTotal: orderGrandTotalKey,
      validateOnline:
        canUseNetwork() && orderId
          ? () =>
              posApi.previewLoyaltyReward({
                customerId,
                rewardId: value,
                orderId: orderId ?? undefined,
                cartItems: cartItemsRef.current,
              })
          : undefined,
    })
      .then((result) => {
        if (cancelled) return;
        setPreview(result);
        onPreviewChangeRef.current?.(
          result.eligible
            ? {
                discountPreview: result.discountPreview,
                pointsCost: result.pointsCost,
                rewardName: result.rewardName,
                eligible: true,
                snapshot: result.snapshot,
                source: result.source,
              }
            : {
                pointsCost: result.pointsCost,
                rewardName: result.rewardName,
                eligible: false,
              },
        );
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    branchId,
    customerId,
    value,
    orderId,
    cartItemsKey,
    orderGrandTotalKey,
  ]);

  if (!customerId || !branchId) {
    return null;
  }

  if (!canUseNetwork() && cacheAvailable === false) {
    return (
      <p className="text-muted-foreground text-xs">
        ບໍ່ມີ cache ລາງວັນ — ກະລຸນາ refresh cache ກ່ອນແລກລາງວັນ offline
      </p>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col gap-2 rounded-md border border-violet-200/80 bg-violet-50/80 p-3 dark:border-violet-900/50 dark:bg-violet-950/20",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-medium text-sm">
          <Gift className="h-4 w-4 text-violet-700 dark:text-violet-300" />
          ແລກລາງວັນ
        </div>
        {balance != null && (
          <span className="text-muted-foreground text-xs tabular-nums">
            ຄະແນນ: {balance.toLocaleString("lo-LA")}
            {!canUseNetwork() && (
              <span className="ml-1 text-[10px]">(cache)</span>
            )}
          </span>
        )}
      </div>

      {listLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          ກຳລັງໂຫຼດລາງວັນ...
        </div>
      ) : rewards.length === 0 ? (
        <p className="text-muted-foreground text-xs">
          {balance != null && balance > 0
            ? "ບໍ່ມີລາງວັນທີ່ແລກໄດ້ — ກວດປະເພດລາງວັນໃນ Admin (POS ຮອງຮັບສ່ວນຫຼຸດ/ສິນຄ້າຟຣີ) ຫຼື refresh cache"
            : "ບໍ່ມີລາງວັນທີ່ແລກໄດ້ (ຄະແນນບໍ່ພຽງພໍ ຫຼື ປິດໃຊ້ງານ)"}
        </p>
      ) : (
        <div className="space-y-2">
          <Label className="sr-only">ເລືອກລາງວັນ</Label>
          <Select
            value={value ?? "__none__"}
            onValueChange={(v) => {
              if (v === "__none__") {
                onChange(null);
              } else {
                onChange(v);
              }
            }}
          >
            <SelectTrigger className="h-9 bg-background text-sm">
              <SelectValue placeholder="ເລືອກລາງວັນ..." />
            </SelectTrigger>
            <SelectContent
              position="popper"
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              <SelectItem value="__none__">ບໍ່ແລກລາງວັນ</SelectItem>
              {rewards.map((r) => (
                <SelectItem key={r.id} value={r.id}>
                  {r.name} ({r.pointsCost.toLocaleString("lo-LA")} ຄະແນນ)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {value && previewLoading && (
            <div className="flex items-center gap-2 text-muted-foreground text-xs">
              <Loader2 className="h-3 w-3 animate-spin" />
              ກຳລັງກວດສອບ...
            </div>
          )}

          {value && preview && !preview.eligible && (
            <p className="text-destructive text-xs">
              {preview.error ?? "ບໍ່ສາມາດແລກລາງວັນນີ້"}
            </p>
          )}

          {value && preview?.eligible && (
            <div className="flex flex-wrap gap-1.5 text-xs">
              <Badge variant="secondary" className="tabular-nums">
                −{preview.pointsCost.toLocaleString("lo-LA")} ຄະແນນ
              </Badge>
              {preview.discountPreview != null && preview.discountPreview > 0 && (
                <Badge variant="outline" className="tabular-nums">
                  ສ່ວນຫຼຸດ ~
                  {preview.discountPreview.toLocaleString("lo-LA")} ₭
                </Badge>
              )}
              {preview.source === "cache" && (
                <Badge variant="outline" className="text-[10px]">
                  cache
                </Badge>
              )}
              <span className="text-muted-foreground">
                ຄົງເຫຼືອ ~{preview.balanceAfter.toLocaleString("lo-LA")} ຄະແນນ
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
